import {BaseLoadingStore} from "app/modules/common/stores/BaseLoadingStore";
import {action, makeObservable, observable} from "mobx";
import {inject} from "react-ioc";
import {AdminApiClients} from "app/modules/admin/services/AxiosBaseClient";

const DOMAIN_FORMAT_REGEX = /^[a-zA-Z0-9]([a-zA-Z0-9\-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9\-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

function isValidDomain(domain: string): boolean {
    const trimmed = (domain || "").trim().toLowerCase();
    return trimmed.length > 0 && DOMAIN_FORMAT_REGEX.test(trimmed);
}

export class TenantDetailsStore extends BaseLoadingStore {
    @inject adminApiClients: AdminApiClients;

    title: string = '';

    TabDetails: string = "Details";
    TabUsers: string = "Users";
    selectedTab: string = this.TabDetails;
    tenantId : number = null;

    verifiedDomains: string[] = [];
    verifiedDomainsDirty: boolean = false;
    newDomainInput: string = '';
    domainValidationError: string = '';

    constructor() {
        super();

        makeObservable(this, {
            title: observable,
            selectedTab: observable,
            tenantId: observable,
            verifiedDomains: observable,
            verifiedDomainsDirty: observable,
            newDomainInput: observable,
            domainValidationError: observable,

            init: action,
            selectTab: action,
            resetJobs: action,
            upgradeTenant: action,
            loadVerifiedDomains: action,
            saveVerifiedDomains: action,
            addDomain: action,
            removeDomain: action,
            setNewDomainInput: action
        });
    }

    resetJobs = async () => {
        await this.wrapLoadingApiCall(async () => {
            await this.adminApiClients.tenantManagementClient.setRecurringJobs(this.tenantId);
        })
    }

    sylectusSync = async () => {
        await this.wrapLoadingApiCall(async () => {
            await this.adminApiClients.tenantManagementClient.runSylectusSync(this.tenantId);
        })
    }

    upgradeTenant = async () => {
        await this.wrapLoadingApiCall(async () => {
            await this.adminApiClients.tenantManagementClient.upgradeTenant(this.tenantId);
        })
    }

    loadVerifiedDomains = async () => {
        if (this.tenantId == null) return;
        const domains = await this.adminApiClients.tenantManagementClient.getVerifiedDomains(this.tenantId);
        this.verifiedDomains = domains || [];
        this.verifiedDomainsDirty = false;
    }

    saveVerifiedDomains = async () => {
        if (this.tenantId == null || !this.verifiedDomainsDirty) return;
        await this.wrapLoadingApiCall(async () => {
            await this.adminApiClients.tenantManagementClient.setVerifiedDomains(this.tenantId, this.verifiedDomains);
            this.verifiedDomainsDirty = false;
        });
    }

    setNewDomainInput = (value: string) => {
        this.newDomainInput = value;
        this.domainValidationError = '';
    }

    addDomain = () => {
        const raw = (this.newDomainInput || "").trim();
        if (!raw) {
            this.domainValidationError = "Enter a domain";
            return;
        }
        const domain = raw.toLowerCase();
        if (!isValidDomain(domain)) {
            this.domainValidationError = "Invalid domain format (e.g. example.com)";
            return;
        }
        if (this.verifiedDomains.includes(domain)) {
            this.domainValidationError = "Domain already in list";
            return;
        }
        this.verifiedDomains = [...this.verifiedDomains, domain];
        this.verifiedDomainsDirty = true;
        this.newDomainInput = '';
        this.domainValidationError = '';
    }

    removeDomain = (domain: string) => {
        this.verifiedDomains = this.verifiedDomains.filter(d => d !== domain);
        this.verifiedDomainsDirty = true;
    }

    init = async (id: number) => {
        await this.wrapInitLoadingApiCall(async () => {
            this.tenantId = id;
            this.title = "First Easy Delivery, Inc";
            await this.loadVerifiedDomains();
            this.isLoading = false;
        })
    }

    selectTab = (tab: string) => this.selectedTab = tab;
}
